export const corporateProductionFallback = {
  page: {
    path: '/corporate-event-production',
    canonicalUrl: 'https://miamisoundrental.com/corporate-event-production'
  },
  revision: {
    seo: {
      title: 'Corporate AV Production in Miami & Fort Lauderdale | MSR',
      description: 'Plan conferences, conventions, meetings, trade shows, and product launches with one South Florida AV production team—from preproduction through show day.',
      canonicalUrl: 'https://miamisoundrental.com/corporate-event-production',
      robots: {
        index: true,
        follow: true
      },
      openGraph: {
        type: 'website',
        url: 'https://miamisoundrental.com/corporate-event-production',
        title: 'Corporate AV Production in Miami & Fort Lauderdale | MSR',
        description: 'One South Florida production team for corporate audio, video, lighting, staging, technical labor, and live show support.',
        images: [{
          url: 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1775822192654.jpg',
          width: 1600,
          height: 1200,
          alt: 'Miami Sound Rental producing the Visa Summit 2026 in South Florida'
        }]
      },
      twitter: {
        cardType: 'summary_large_image'
      }
    },
    content: {
      hero: {
        eyebrow: 'Corporate AV production · Miami & Fort Lauderdale',
        title: 'Corporate AV & Event Production in South Florida',
        description: 'One South Florida AV production company for audio, video, lighting, staging, technical labor, venue coordination, and live show support across Miami and Fort Lauderdale.',
        image: '/Pictures/corporate-event.jpg',
        imageAlt: 'Visa Summit 2026 corporate conference room with dual presentation screens produced by Miami Sound Rental',
        primaryAction: 'Request a Production Proposal',
        secondaryAction: 'Call a Producer'
      },
      proof: [
        { key: 'events', value: '—', label: 'Events serviced this year' },
        { key: 'rating', value: '4.8 / 5', label: 'Current Google rating' },
        { key: 'reviews', value: '268', label: 'Google reviews' },
        { key: 'coverage', value: 'South Florida', label: 'Local production coverage' }
      ],
      paths: {
        eyebrow: 'Choose the right planning path',
        title: 'Production support or preliminary pricing—both start here.',
        description: 'As a corporate event production company, MSR provides a managed scope and accountable production team. Buyers who are still exploring can build a preliminary plan without committing to a final production design.',
        managed: {
          label: 'Recommended for corporate events',
          title: 'Need a managed production?',
          description: 'Share your date, venue, audience, and program. A producer will help define the technical scope, staffing, logistics, and next steps.',
          action: 'Request a Production Proposal'
        },
        planning: {
          label: 'Preliminary planning',
          title: 'Need pricing before you call?',
          description: 'Use the Event Builder to assemble a preliminary equipment plan and see transparent pricing. Our team can review it before show day.',
          action: 'Build a Preliminary Event Plan'
        }
      },
      eventTypes: {
        eyebrow: 'Built around the program',
        title: 'Conference AV, convention AV, and corporate event production',
        description: 'The room, audience, agenda, venue, and presenter experience determine the production—not a preselected equipment package.',
        items: [
          { title: 'Conferences & conventions', description: 'General sessions, multiple rooms, speaker support, show calling, and coordinated technical teams.' },
          { title: 'Corporate meeting AV', description: 'Executive meetings, annual meetings, town halls, training programs, and internal communications.' },
          { title: 'Trade shows & activations', description: 'Exhibit audio, displays, presentation areas, stage moments, and multi-vendor coordination.', href: '/trade-show-av-services', action: 'Explore trade show AV' },
          { title: 'Product launch production', description: 'Reveal moments, playback, lighting cues, presentation support, and brand-led scenic environments.' },
          { title: 'General session AV', description: 'Room-wide sound, presentation video, confidence monitoring, stage lighting, and operator support.' },
          { title: 'Breakout room AV', description: 'Consistent presentation systems, microphones, recording, room turns, and technician coverage.' }
        ]
      },
      capabilities: {
        eyebrow: 'One accountable production partner',
        title: 'AV production services coordinated as one production',
        description: 'Corporate teams comparing AV production companies in Miami usually need more than equipment. MSR operates as one audiovisual production company, bringing event audiovisual services, technical labor, venue coordination, and live show operation into one accountable scope.',
        items: [
          { icon: 'audio', title: 'Audio & communications', description: 'Speech reinforcement, playback, microphones, monitoring, intercom, and room coverage.' },
          { icon: 'video', title: 'Video & presentation', description: 'Projection, LED video walls, displays, switching, playback, confidence monitors, and recording.' },
          { icon: 'lighting', title: 'Lighting', description: 'Stage, room, scenic, presenter, and camera-conscious lighting designed around the program.' },
          { icon: 'staging', title: 'Staging & scenic support', description: 'Stages, risers, lecterns, truss, drape, technical surrounds, and presenter environments.' },
          { icon: 'crew', title: 'Technical labor & show operation', description: 'Producers, project managers, technical directors, operators, technicians, and show-day crew.' },
          { icon: 'streaming', title: 'Streaming & recording', description: 'Hybrid participation, live streaming, isolated records, program feeds, and remote contributors.' }
        ]
      },
      caseStudies: [
        {
          slug: 'visa-summit-2026',
          title: 'Visa Summit 2026',
          type: 'Corporate summit · Loews Coral Gables',
          description: 'A multi-discipline conference production combining sound, visuals, staging, DJ equipment, live streaming, and a 13-person project crew.',
          crew: '13-person crew',
          scope: '5 service areas',
          units: '219 documented units',
          fallbackImage: 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1775822192654.jpg'
        },
        {
          slug: 'blockchain-buzz-at-daer',
          title: 'Blockchain Buzz at DAER',
          type: 'Multi-room conference · Hard Rock Hotel',
          description: 'Coordinated audio, visual, staging, and production infrastructure across multiple conference and exhibition spaces.',
          crew: '9-person crew',
          scope: '4 service areas',
          units: '66 documented units',
          fallbackImage: 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1762379502703.jpg'
        },
        {
          slug: 'af-usa-annual-convention',
          title: 'AF USA Annual Convention',
          type: 'Annual convention · Ritz-Carlton Coconut Grove',
          description: 'Presentation audio, projection, video switching, and event support for a professional annual convention.',
          crew: '5-person crew',
          scope: '3 service areas',
          units: '21 documented units',
          fallbackImage: 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1761425337292.jpg'
        }
      ],
      process: {
        eyebrow: 'One team from brief through strike',
        title: 'A production process designed to reduce buyer risk',
        description: 'Your producer stays connected to the technical and operational details so the event does not become a collection of disconnected rentals.',
        image: '/Pictures/Corporate-team.jpg?v=20260818',
        imageAlt: 'Miami Sound Rental show operator monitoring audio and presentation video during a corporate event',
        steps: [
          { title: 'Discovery & scope', description: 'We review the program, venue, audience, timing, stakeholders, and technical priorities.' },
          { title: 'Preproduction', description: 'The team develops equipment, labor, drawings, schedules, cues, logistics, and venue requirements.' },
          { title: 'Load-in & rehearsal', description: 'Systems are installed, tested, rehearsed, adjusted, and handed over to the show team.' },
          { title: 'Show operation & strike', description: 'Operators support presenters and cues through the event, followed by an organized strike.' }
        ]
      },
      venue: {
        eyebrow: 'Hotel & venue coordination',
        title: 'Plan around the venue before load-in begins',
        description: 'We coordinate the details that can otherwise surprise an event buyer: access windows, loading docks, power, internet, rigging, room turns, labor requirements, insurance documents, and in-house AV rules.',
        image: '/Pictures/corporate-video.jpg?v=20260818b',
        imageAlt: 'Global Family Office Investment Summit panel presented on a large LED video wall in Miami',
        items: [
          'Venue communication and technical advance',
          'Load-in, dock, elevator, and access scheduling',
          'Power, internet, rigging, and room-layout review',
          'Certificates of insurance and vendor documentation',
          'Rehearsal, room turns, and show-day timing'
        ]
      },
      procurement: {
        id: 'corporate-procurement',
        eyebrow: 'Corporate qualification & procurement',
        heading: 'A production partner your event team and purchasing team can work with',
        body: 'Technical delivery and vendor approval are part of the same project. MSR can document the production scope, coordinate venue requirements, and support the purchasing information needed to move an approved event forward.',
        qualificationTitle: 'Corporate production qualification',
        qualificationItems: [
          'Audio, video, lighting, staging, streaming, and technical crews in one coordinated scope',
          'Conference, general-session, breakout-room, brand-event, and multi-room production experience',
          'Venue advances, access planning, rehearsals, show operation, and strike coordination',
          'One accountable production contact across stakeholders and technical disciplines'
        ],
        procurementTitle: 'Vendor & purchasing support',
        procurementItems: [
          'Itemized proposals and estimates that can be shared with internal approvers',
          'Current W-9 and evidence-of-insurance documentation',
          'Custom certificate-of-insurance support for eligible confirmed productions',
          'ACH, wire, debit, and credit-card payments',
          'Tax-exempt purchasing support with certificate review',
          'Payment terms for approved business accounts'
        ],
        note: 'Have a vendor-onboarding checklist, certificate-holder requirements, or purchasing deadline? Add it to the proposal request so the producer can coordinate the appropriate documentation and timing.',
        primaryAction: 'Request a Production Proposal',
        secondaryAction: 'Email Procurement Requirements'
      },
      faqs: [
        { question: 'What does a full-service corporate AV production include?', answer: 'A managed production can include discovery, system design, equipment, technical labor, venue coordination, load-in, testing, rehearsal, live show operation, and strike. The exact scope depends on the program, rooms, venue, audience, schedule, and services required.' },
        { question: 'Can you support a general session and breakout rooms together?', answer: 'Yes. We can plan the main room and breakout rooms as one coordinated production, including consistent presentation systems, microphones, technicians, schedules, room turns, and content handoffs.' },
        { question: 'Do you work with hotel and venue event teams?', answer: 'Yes. We regularly coordinate access, power, internet, rigging, labor rules, insurance documents, room layouts, rehearsal windows, and in-house AV requirements with hotels and venues.' },
        { question: 'How early should we request a corporate production proposal?', answer: 'Contacting us as soon as the date and venue are being considered provides the most planning flexibility. Complex, multi-room, streamed, staged, or high-attendance events benefit from a longer preproduction window, but our team can also evaluate time-sensitive requests.' },
        { question: 'Can I see preliminary pricing before speaking with a producer?', answer: 'Yes. The Event Builder can create a preliminary equipment and pricing plan. It is useful for early budgeting, but a producer should review venue, labor, timing, power, internet, staging, and show-operation requirements before the plan is considered final.' },
        { question: 'Which areas do you serve?', answer: 'Miami Sound Rental supports corporate events across Miami, Fort Lauderdale, and South Florida. Larger productions outside the immediate area can be reviewed based on the venue, schedule, and project requirements.' },
        { key: 'corporate-procurement-documents', question: 'Can you provide a W-9, COI, and vendor-onboarding documentation?', answer: 'Yes. We can provide a current W-9 and evidence-of-insurance documentation, coordinate custom certificate-of-insurance requests for eligible confirmed productions, and work through venue or vendor requirements. Custom additional-insured certificates may require a confirmed order and complete certificate-holder details.' },
        { key: 'corporate-procurement-payment', question: 'Which corporate payment and approval options do you support?', answer: 'Corporate customers can receive itemized proposals and shareable estimates. ACH, wire, debit, and credit-card payments are accepted. Tax-exemption certificates can be submitted for review, and payment terms are available to approved business accounts.' }
      ],
      proposal: {
        eyebrow: 'Start the production conversation',
        title: 'Request a corporate production proposal',
        description: 'Share the information you already have. A producer will review the event, clarify the technical scope, and help define the next step.'
      },
      builder: {
        eyebrow: 'Still in the budgeting stage?',
        title: 'Build a preliminary event plan online',
        description: 'Choose equipment, dates, and a location to explore pricing. The result is a planning starting point—not a substitute for technical review on a complex corporate production.',
        action: 'Open the Event Builder'
      },
      related: {
        eyebrow: 'Related production services',
        title: 'Continue with the production path that fits the program',
        items: [
          { title: 'Live Streaming Production', description: 'Hybrid events, remote presenters, multi-camera programs, and live distribution.', href: '/production-services/live-streaming' },
          { title: 'Concert Production', description: 'Live-performance audio, staging, lighting, visuals, and technical operation.', href: '/concert-production' },
          { title: 'Private Event Production', description: 'Managed AV production for galas, celebrations, and private programs.', href: '/private-events' },
          { title: 'Production Portfolio', description: 'Review real MSR productions, documented scope, crews, services, and equipment.', href: '/portfolio' }
        ]
      }
    }
  }
}

export default corporateProductionFallback
